import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';

import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import { modalBg } from '@v2/pages/common/constants/base.constants';
import ColorsWrapper from '@styles/colors';
import Button from '@styles/buttons';
import { navigationRef } from '@helpers';
import { detectCurrentPage } from './pageConfig';
import { useTourContext } from '@components/tours/components/TourProvider';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { hasTourId, getPageDisplayName } from './helpers';
import type { WithColorProps } from '@styles/colors/types';
import { useSelector } from 'react-redux';
import { selectIsWithERP } from '@reducers/membership';
import { getSecureValue, withERP } from '@helpers';
import { WELCOME_TOUR_COMPLETED_KEY } from '@components/tours/constants';

interface SupportDropdownProps extends Pick<WithColorProps, 'C'> {
  triggerSupportMenu: (value?: boolean) => void;
}

const SupportDropdown: React.FC<SupportDropdownProps> = ({ C, triggerSupportMenu }) => {
  const { startTour, startGlobalTour } = useTourContext();
  const isWithERP = useSelector(selectIsWithERP);
  const pageConfig = useMemo(() => {
    const routeName = navigationRef?.current?.getCurrentRoute()?.name;
    return detectCurrentPage(routeName);
  }, []);

  const handleTakeTour = () => {
    if (pageConfig?.tourId) {
      startTour(pageConfig.tourId);
    }
    triggerSupportMenu();
  };

  const handleTakeWelcomeTour = () => {
    const hasCompletedBefore = !!getSecureValue(WELCOME_TOUR_COMPLETED_KEY);
    trackEvent(TRACKING_EVENTS.WELCOME_TOUR, {
      action: 'started',
      isFirstTimeUser: !hasCompletedBefore,
      hasERPIntegration: withERP(isWithERP),
    });
    startGlobalTour('welcome-tour');
    triggerSupportMenu();
  };

  const hasTour = hasTourId(pageConfig);
  const pageName = getPageDisplayName(pageConfig);

  return (
    <TouchableOpacity
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: C('Overlay/Light/Dim strong'),
        position: 'absolute',
        zIndex: 1000,
      }}
      onPress={() => triggerSupportMenu()}
      activeOpacity={1}
    >
      <View
        style={{
          width: '100%',
          height: '100%',
          alignItems: 'center',
        }}
      >
        <View
          style={{
            width: '100%',
            paddingRight: 18,
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}
        >
          <View
            style={[
              getBoxShadowStyle({ type: 'Narrow', C }),
              {
                backgroundColor: C(modalBg),
                width: 280,
                borderRadius: 9,
                borderWidth: 1,
                borderColor: C('Hairline/Light/Primary'),
                marginTop: 4,
                alignItems: 'center',
              },
            ]}
          >
            <View style={{ marginTop: 18 }}>
              <Text style={[font['Headline'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>
                {i18n.t('Help & Support')}
              </Text>
            </View>

            {!pageConfig ? (
              <View style={{ marginTop: 16, marginBottom: 18, paddingLeft: 20, paddingRight: 20, width: '100%' }}>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Secondary'), textAlign: 'center', marginBottom: 16 }]}>
                  {i18n.t('No help available for this page')}
                </Text>
                <Button
                  {...{
                    variant: 'Dialog M',
                    type: 'Secondary',
                    onPress: handleTakeWelcomeTour,
                  }}
                >
                  {i18n.t('Quick Welcome Tour')}
                </Button>
              </View>
            ) : (
              <>
                {hasTour && (
                  <View
                    style={{
                      marginTop: 18,
                      paddingLeft: 20,
                      paddingRight: 20,
                      width: '100%',
                    }}
                  >
                    <Button
                      {...{
                        variant: 'Dialog M',
                        type: 'Primary',
                        onPress: handleTakeTour,
                      }}
                    >
                      {`${i18n.t('Take the')} ${pageName} ${i18n.t('Tour')}`}
                    </Button>
                  </View>
                )}
                <View
                  style={{
                    marginTop: 18,
                    paddingLeft: 20,
                    paddingRight: 20,
                    width: '100%',
                  }}
                >
                  <Button
                    {...{
                      variant: 'Dialog M',
                      type: 'Secondary',
                      onPress: handleTakeWelcomeTour,
                    }}
                  >
                    {i18n.t('Quick Welcome Tour')}
                  </Button>
                </View>
                <View style={{ height: 18 }} />
              </>
            )}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default ColorsWrapper(SupportDropdown);
